import { Voucher } from '../types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails, formatVoucherDate } from './helpers';

/**
 * Build pdfInfo for Deposit
 *
 * Fixes from audit:
 * - Column title "Payment Method" (was "FOP") — matches inline
 * - Full paymentMethodType (was abbreviated CC/BT/Cash)
 * - isDeposited: true flag
 * - Consistent date formatting via formatVoucherDate
 */
export function buildDepositPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const paymentObj = info?.paymentMethodObject || {};

    const bankAccountName = info?.accountName || paymentObj?.bankName || paymentObj?.receivingBank?.bankName || '';
    const bankAccountNumber = paymentObj?.accountNumber || paymentObj?.receivingBank?.accountNumber || paymentObj?.bankAccountNumber || '';

    // ── Columns: match inline (Receipt#, Payment Method, Details, Value Date, Amount, Currency) ──
    const columns: PdfTablePropsColumn[] = [
        { title: 'Receipt#', fieldName: 'receiptNumber', style: { width: '15%' } },
        { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '15%' } },
        { title: 'Details', fieldName: 'details', style: { width: '25%' } },
        { title: 'Value Date', fieldName: 'valueDate', style: { width: '15%' } },
        { title: 'Amount', fieldName: 'amount', style: { width: '18%' } },
        { title: 'Currency', fieldName: 'currency', style: { width: '12%' } }
    ];

    // ── Build rows from covered receipts ─────────────────────────────────────
    const coveredDocuments = context.coveredDocuments || [];
    let totalAmount = 0;

    const rows: PdfTablePropsRow[] = coveredDocuments.map((coveredDoc) => {
        const coveredInfo = (Array.isArray(coveredDoc.info) ? coveredDoc.info[0] : coveredDoc.info) as any;
        const docAmount = Number(coveredInfo?.amount) || 0;
        totalAmount += docAmount;

        const { code: docCurrencyCode } = getCurrencyDetails(coveredInfo?.currency, context.currencies);
        const coveredPaymentObj = coveredInfo?.paymentMethodObject;
        const paymentMethodType = coveredInfo?.paymentMethodType || 'Payment';

        // Build details: check details with bank, account, number (matching inline)
        let details = '';
        if (paymentMethodType === 'Check' && coveredPaymentObj?.checks) {
            const rawChecks = coveredPaymentObj.checks;
            const checksArr = Array.isArray(rawChecks) ? rawChecks : [rawChecks];
            details = checksArr.map((c: any) =>
                [c.bank || c.bankCode, c.account || c.accountNumber, c.number].filter(Boolean).join(', ')
            ).join('; ');
        } else if (coveredPaymentObj?.cardNo) {
            details = `****${coveredPaymentObj.cardNo.slice(-4)}`;
        } else if (coveredPaymentObj?.reference) {
            details = coveredPaymentObj.reference;
        } else {
            details = coveredInfo?.for || coveredInfo?.description || '';
        }

        const valueDate = coveredPaymentObj?.valueDate || coveredInfo?.dueDate;

        return {
            receiptNumber: String(coveredDoc.number || ''),
            paymentMethod: paymentMethodType,
            details,
            valueDate: formatVoucherDate(valueDate),
            amount: taytanFormatNumberToString(docAmount),
            currency: docCurrencyCode
        };
    });

    // Fallback: single row if no covered documents
    if (rows.length === 0) {
        const { code: currencyCode } = getCurrencyDetails(info?.currency, context.currencies);
        const amount = Number(info?.amount) || 0;
        totalAmount = amount;
        rows.push({
            receiptNumber: '',
            paymentMethod: info?.paymentMethodType || '',
            details: info?.for || info?.description || '',
            valueDate: formatVoucherDate(info?.dueDate),
            amount: taytanFormatNumberToString(amount),
            currency: currencyCode
        });
    }

    const firstDoc = coveredDocuments[0];
    const firstDocInfo = firstDoc ? (Array.isArray(firstDoc.info) ? firstDoc.info[0] : firstDoc.info) : null;
    const summaryCurrency = getCurrencyDetails((firstDocInfo as any)?.currency || info?.currency, context.currencies).code;

    const summaryLine: PdfTablePropsRow = {
        receiptNumber: '',
        paymentMethod: '',
        details: '',
        valueDate: 'Total',
        amount: taytanFormatNumberToString(totalAmount),
        currency: summaryCurrency
    };

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        topLeftDetailsText: bankAccountName,
        bankAccountNumber,
        bottomDetails: info?.for || '',
        isDeposited: true,
        usesValueDate: true
    };
}

/**
 * Build pdfInfo for Withdrawal
 *
 * Fixes from audit:
 * - Added agentInfo/supplierInfo from first covered document
 * - Added isWithdrawn: true flag
 */
export function buildWithdrawalPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const depositInfo = buildDepositPdfInfo(voucher, context, baseInfo);

    // Override: Withdrawal doesn't show bank account at top
    delete depositInfo.topLeftDetailsText;
    delete depositInfo.bankAccountNumber;
    delete depositInfo.isDeposited;

    // Extract agentInfo/supplierInfo/travelFileNumber from first covered document
    const coveredDocuments = context.coveredDocuments || [];
    const firstDoc = coveredDocuments[0];
    if (firstDoc) {
        const firstDocInfo = (Array.isArray(firstDoc.info) ? firstDoc.info[0] : firstDoc.info) as any;
        const storedPdfInfo = firstDocInfo?.pdfInfo;
        if (storedPdfInfo?.agentInfo) {
            depositInfo.agentInfo = storedPdfInfo.agentInfo;
        }
        if (storedPdfInfo?.supplierInfo) {
            depositInfo.supplierInfo = storedPdfInfo.supplierInfo;
        }
        // Resolve travelFileNumber from covered receipt if not on withdrawal itself
        if (!depositInfo.travelFileNumber && firstDocInfo?.travelFileNumber) {
            depositInfo.travelFileNumber = firstDocInfo.travelFileNumber;
        }
    }

    depositInfo.isWithdrawn = true;

    return depositInfo;
}
